import { type Display, format, type Notation } from '@seek/currency';
import type { SalaryCurrency as SalaryCurrencyType } from '@seek/salary-data';
import { maxLength } from '@seek/validators-js';
import { useTranslations } from '@vocab/react';
import { Stack } from 'braid-design-system';
import {
  forwardRef,
  useState,
  useEffect,
  type FocusEvent,
  type FormEvent,
  type ChangeEvent,
  type Ref,
  useImperativeHandle,
  useCallback,
  useRef,
} from 'react';
import { shallowEqual, useDispatch, useSelector } from 'react-redux';

import { TEALIUM_CAJA_PAY_TYPE } from 'src/app/shared/constants/dispatcherId';
import { FORM_VALIDATION_ERROR } from 'src/app/shared/constants/errorMessages';
import {
  PAY_RANGE_PRESSED,
  TOOLTIP_PRESSED,
  PAY_DISPLAYED_PRESSED,
  PAY_TYPE_PRESSED,
  SALARY_HIDE,
  TOGGLE_PRESSED,
} from 'src/app/shared/dataAnalytics/schema/constants';
import { useConfig } from 'src/app/shared/hooks/useConfig';
import { getClassificationDataByAdCenterIds } from 'src/app/utils/classifications';
import { isANZCountryCode } from 'src/app/utils/country';
import { removeEmoji } from 'src/app/utils/emoji';
import { isPositiveNumber } from 'src/app/utils/isPositiveNumber';
import { normalizeNumber, parseNumber } from 'src/app/utils/number';
import { getSalaryCurrency } from 'src/app/utils/salary/getSalary';
import {
  constructDefaultSalaryDisplayText,
  formatSalaryWithCurrency,
} from 'src/app/utils/salary/utils/formatSalary';
import {
  filterContent,
  validateField,
  type Validators,
} from 'src/app/utils/validation';
import {
  EVENT_NAME_ALERT_VIEWED,
  EVENT_TYPE_CURRENCY_TYPE_SELECTED,
} from 'src/app/v1/analytics';
import getTrackingAdapter from 'src/app/v1/dataAnalytics';
import { sendContentSafetyTrackingEvents } from 'src/app/v1/dataAnalytics/utils/sendContentSafetyTrackingEvents';
import dispatchTealium from 'src/app/v1/state/dispatcher/tealium';
import { actionUpdateDisplayPay } from 'src/app/v1/state/modules/formData/displayPay';
import { actionUpdateMaxSalary } from 'src/app/v1/state/modules/formData/maxSalary';
import { actionUpdateMinSalary } from 'src/app/v1/state/modules/formData/minSalary';
import { actionUpdatePayType } from 'src/app/v1/state/modules/formData/payType';
import { actionUpdateSalaryCurrency } from 'src/app/v1/state/modules/formData/salaryCurrency';
import { actionUpdateSalaryText } from 'src/app/v1/state/modules/formData/salaryText';
import { actionUpdateCajaSalaryCurrency } from 'src/app/v1/state/modules/tealium/cajaSalaryCurrency';
import { actionUpdateTrackingDisplayPay } from 'src/app/v1/state/modules/tealium/displayPay';
import { actionUpdateRecommendedSalaryRange } from 'src/app/v1/state/modules/tealium/recommendedSalaryRange';
import { actionUpdateSalaryRangeSelected } from 'src/app/v1/state/modules/tealium/salaryRangeSelected';
import { AbsurdSalaryMessage } from 'src/app/v1/views/components/Pay/components/AbsurdSalaryMessage';
import { GoodSalaryMessage } from 'src/app/v1/views/components/Pay/components/GoodSalaryMessage';
import { LowSalaryMessage } from 'src/app/v1/views/components/Pay/components/LowSalaryMessage';
import { PayRange } from 'src/app/v1/views/components/Pay/components/PayRange';
import { PayRangeRecommendation } from 'src/app/v1/views/components/Pay/components/PayRangeRecommendation';
import { PayShownToggle } from 'src/app/v1/views/components/Pay/components/PayShownToggle';
import { PayType } from 'src/app/v1/views/components/Pay/components/PayType';
import { useSyncCurrencyPayType } from 'src/app/v1/views/components/Pay/hooks/useSyncCurrencyPayType';
import { appendDecimalPart } from 'src/app/v1/views/components/Pay/util/appendDecimalPart';
import { getPayDisplayApplicationPercentage } from 'src/app/v1/views/components/Pay/util/getPayDisplayApplicationPercentage';
import { isNumberOrNullOrDecimalSeparator } from 'src/app/v1/views/components/Pay/util/isNumberOrNullOrDecimalSeparator';
import { judgeSalaryRange } from 'src/app/v1/views/components/Pay/util/judgeSalaryRange';
import { makeSalaryDataValidator } from 'src/app/v1/views/components/Pay/validation/makeSalaryDataValidator';
import { useEmojiRestrictionsFeature } from 'src/app/v1/views/hooks/useEmojiRestrictionsFeature';
import { useSuggestedSalary } from 'src/app/v1/views/hooks/useSuggestedSalary';
import type { AppState } from 'src/types/appState';

import translations from './.vocab';

export const Pay = forwardRef((_props, ref: Ref<any>) => {
  // Hooks
  const dispatch = useDispatch();
  const { t } = useTranslations(translations);
  const locale = useConfig().getLocale();
  const { getLanguage } = useConfig();
  const { shouldRestrictEmojis } = useEmojiRestrictionsFeature();
  const { suggestedSalary } = useSuggestedSalary();
  const {
    jobTitle,
    categoryId,
    subCategoryId,
    hirerNation,
    payTypeOriginal,
    salaryText,
    displayPay,
    jobLocationCountry,
    maxSalaryOriginal,
    minSalaryOriginal,
  } = useSelector(
    (state: AppState) => ({
      jobTitle: state.formData.jobTitle,
      categoryId: state.formData.categoryId,
      subCategoryId: state.formData.subCategoryId,
      hirerNation: state.session.hirerAccount.nation,
      payTypeOriginal: state.formData.payType,
      salaryText: state.formData.salaryText,
      displayPay: state.formData.displayPay,
      jobLocationCountry: state.session.jobLocationCountry,
      maxSalaryOriginal: state.formData.maxSalary,
      minSalaryOriginal: state.formData.minSalary,
    }),
    shallowEqual,
  );
  const { currency, frequency, payType } = useSyncCurrencyPayType({
    onResetSalary: () => {
      setMinSalaryText('');
      setMaxSalaryText('');
      setSalaryErrorMin(undefined);
      setSalaryErrorMax(undefined);
      setSalaryError(undefined);
      setMinimumSalary(null);
      setMaximumSalary(null);
    },
  });
  const { averageMin, averageMax } = suggestedSalary || {};
  const dispatchSalaryDetails = useCallback(
    ({
      maxValue,
      minValue,
      text,
    }: {
      maxValue?: number | null;
      minValue?: number | null;
      text?: string;
    }) => {
      dispatch(actionUpdateMaxSalary(maxValue!));
      dispatch(actionUpdateMinSalary(minValue!));

      if (text) {
        dispatch(actionUpdateSalaryText(text));
      }

      const isSuggestedSalaryRangeSelected =
        minValue === averageMin && maxValue === averageMax;

      dispatch(actionUpdateSalaryRangeSelected(isSuggestedSalaryRangeSelected));
    },
    [dispatch, averageMin, averageMax],
  );
  const previousCurrency = useRef<string>(currency);

  // State
  const [payTypeText, setPayTypeText] = useState(payTypeOriginal);
  const [minSalaryText, setMinSalaryText] = useState<string>('');
  const [maxSalaryText, setMaxSalaryText] = useState<string>('');
  const [minimumSalary, setMinimumSalary] = useState<number | undefined | null>(
    minSalaryOriginal,
  );
  const [maximumSalary, setMaximumSalary] = useState<number | undefined | null>(
    maxSalaryOriginal,
  );
  const [salaryError, setSalaryError] = useState<string | null | undefined>(
    null,
  );
  const [salaryErrorMin, setSalaryErrorMin] = useState<
    string | null | undefined
  >(undefined);
  const [salaryErrorMax, setSalaryErrorMax] = useState<
    string | null | undefined
  >(undefined);
  const [payDisplayToggleValue, setPayDisplayToggleValue] = useState<
    'show' | 'hide'
  >(displayPay ? 'show' : 'hide');
  const [salaryDetails, setSalaryDetails] = useState<string>(salaryText);
  const [payShownValidationMessage, setPayShownValidationMessage] = useState<
    string | null
  >(null);

  // Derived state
  const hasValidationError =
    Boolean(payShownValidationMessage) ||
    Boolean(salaryError) ||
    Boolean(salaryErrorMin) ||
    Boolean(salaryErrorMax);
  const salaryJudgement = judgeSalaryRange({
    hasValidationError,
    minimumSalary,
    maximumSalary,
    suggestedSalary,
  });
  const country = (jobLocationCountry ?? hirerNation ?? 'AU').toLowerCase();
  const isANZ: boolean = isANZCountryCode(jobLocationCountry || hirerNation);
  // Calculate the application percentage relevant to the country
  // the job is being posted into
  const applicationPercentage = getPayDisplayApplicationPercentage(
    jobLocationCountry || hirerNation || 'au',
  );
  const hasApplicationPercentage =
    typeof applicationPercentage === 'number' && applicationPercentage > 0;
  const payDisplayToggleAlertText = hasApplicationPercentage
    ? t('pay-display-toggle-alert-text-with-percentage', {
        percentage: applicationPercentage.toString(),
      })
    : t('pay-display-toggle-alert-text');
  const absurdSalaryWarning = t('absurd-salary-warning');
  const salaryCurrency = getSalaryCurrency({
    formDataCurrency: currency,
    hirerNation,
    jobLocationCountry,
  });
  const FORMAT_OPTIONS: {
    display: Display;
    notation: Notation;
    dropFraction: boolean;
  } = {
    display: 'narrowSymbol',
    notation: 'standard',
    dropFraction: true,
  };
  const minSalaryRecommended = format(suggestedSalary?.averageMin ?? 0, {
    locale,
    currency,
    ...FORMAT_OPTIONS,
  });

  const maxSalaryRecommended = format(suggestedSalary?.averageMax ?? 0, {
    locale,
    currency,
    ...FORMAT_OPTIONS,
  });

  // Validators
  const handleSalaryValidation = makeSalaryDataValidator(
    t,
    currency,
    frequency,
    { setSalaryError, setSalaryErrorMax, setSalaryErrorMin },
  );
  const handleSalaryTextValidation = (
    value: string,
    skipIfNull: boolean = false,
  ): string | null => {
    const validators: Validators = [maxLength, filterContent];

    const validationResult = validateField(
      {
        field: 'payDisplayText',
        validators,
        validatorProps: {
          max: 50,
          country,
          sendTrackingEvents: sendContentSafetyTrackingEvents,
        },
        value,
      },
      skipIfNull,
      getLanguage(),
    );
    setPayShownValidationMessage(validationResult);
    return validationResult;
  };
  useImperativeHandle(ref, () => ({
    handleValidation: (skipIfNull: boolean) =>
      [
        handleSalaryTextValidation(salaryDetails, skipIfNull),
        handleSalaryValidation({
          maxSalary: maximumSalary,
          maxSalaryText,
          minSalary: minimumSalary,
          skipIfNull,
          validationType: undefined,
          value: undefined,
        }),
      ]
        .filter(Boolean)
        .join('\n') || null,
  }));

  // Handlers for various UI elements
  const handleSalaryCurrencyOnChange = (e: ChangeEvent<HTMLSelectElement>) => {
    const newCurrency = e.target.value;
    dispatch(actionUpdateSalaryCurrency(newCurrency as SalaryCurrencyType));
    dispatch(actionUpdateCajaSalaryCurrency(newCurrency));
    getTrackingAdapter.sendLinkEvent({
      eventName: EVENT_TYPE_CURRENCY_TYPE_SELECTED,
      previousCurrency: previousCurrency.current,
      cajaSalaryCurrency: newCurrency,
    });
  };
  const handlePayTypeChanged = (event: FormEvent<HTMLInputElement>) => {
    const { value } = event.currentTarget;

    setPayTypeText(value);
    setSalaryDetails('');

    dispatch(actionUpdatePayType(value));
    dispatch(dispatchTealium(TEALIUM_CAJA_PAY_TYPE, value));

    const eventData = {
      eventName: PAY_TYPE_PRESSED,
      objectInteraction: `pay-type-${value}`,
      jobSalaryType: value,
    };
    getTrackingAdapter.sendLinkEvent(eventData);
  };
  const handleToggleContextualHelp = () => {
    const eventData = {
      eventName: TOOLTIP_PRESSED,
      tooltipType: 'wage legal requirements',
    };
    getTrackingAdapter.sendLinkEvent(eventData);
  };
  const handleUpdateMinimumSalary = (event: ChangeEvent<HTMLInputElement>) => {
    const valid = isNumberOrNullOrDecimalSeparator(event);

    if (!valid) {
      return;
    }

    const value = event.target.value;
    const minSalaryValue = parseNumber(value);

    const minSalText = appendDecimalPart(
      value,
      formatSalaryWithCurrency(minSalaryValue, currency),
    );
    const maxSalText = formatSalaryWithCurrency(maximumSalary, currency);

    setMinSalaryText(minSalText);
    setMaxSalaryText(maxSalText);

    const salaryTextDetails = constructDefaultSalaryDisplayText({
      min: minSalaryValue,
      max: maximumSalary,
      currency,
      payType,
    });

    const errors = handleSalaryValidation({
      minSalary: minSalaryValue,
      maxSalary: maximumSalary,
      maxSalaryText,
      validationType: 'min',
      value: isNaN(minSalaryValue) ? '' : minSalaryValue.toString(),
    });

    setMinimumSalary(minSalaryValue);
    if (Boolean(errors)) return;
    setSalaryDetails(salaryTextDetails);
    dispatchSalaryDetails({
      maxValue: maximumSalary,
      minValue: minSalaryValue,
      text: salaryTextDetails,
    });
  };
  const handleMinSalaryChange = (e: FormEvent<HTMLInputElement>) =>
    handleUpdateMinimumSalary &&
    handleUpdateMinimumSalary(e as ChangeEvent<HTMLInputElement>);

  const handleUpdateMaximumSalary = (event: ChangeEvent<HTMLInputElement>) => {
    const valid = isNumberOrNullOrDecimalSeparator(event);

    if (!valid) {
      return;
    }

    const value = event.target.value;
    const maxSalaryValue = parseNumber(value);

    const minSalText = formatSalaryWithCurrency(minimumSalary, currency);
    const maxSalText = appendDecimalPart(
      value,
      formatSalaryWithCurrency(maxSalaryValue, currency),
    );

    setMinSalaryText(minSalText);
    setMaxSalaryText(maxSalText);

    const salaryTextDetails = constructDefaultSalaryDisplayText({
      min: minimumSalary,
      max: maxSalaryValue,
      currency,
      payType,
    });

    const errors = handleSalaryValidation({
      minSalary: minimumSalary,
      maxSalary: maxSalaryValue,
      maxSalaryText,
      validationType: 'max',
      value: isNaN(maxSalaryValue) ? '' : maxSalaryValue.toString(),
    });

    setMaximumSalary(maxSalaryValue);
    if (Boolean(errors)) return;
    setSalaryDetails(salaryTextDetails);
    dispatchSalaryDetails({
      maxValue: maxSalaryValue,
      minValue: minimumSalary,
      text: salaryTextDetails,
    });
  };
  const handleMaxSalaryChange = (e: FormEvent<HTMLInputElement>) =>
    handleUpdateMaximumSalary &&
    handleUpdateMaximumSalary(e as ChangeEvent<HTMLInputElement>);

  const sendSalaryUpdateLinkEvent = (isMinSalaryUpdated: boolean) => {
    const eventData = {
      eventName: PAY_RANGE_PRESSED,
      objectInteraction: `salary-${isMinSalaryUpdated ? 'min' : 'max'}`,
    };
    getTrackingAdapter.sendLinkEvent(eventData);
  };
  const handleSalaryMaxOnBlur = (event: FocusEvent<HTMLInputElement>) => {
    sendSalaryUpdateLinkEvent(false);

    const validationResult = handleSalaryValidation({
      minSalary: minimumSalary,
      maxSalary: maximumSalary,
      maxSalaryText,
      validationType: 'max',
      value: normalizeNumber(event.target.value),
    });

    if (validationResult) {
      const eventData = {
        eventName: FORM_VALIDATION_ERROR,
        rangeType: 'max',
        action: 'blur',
        errorMessage: validationResult,
      };
      getTrackingAdapter.sendLinkEvent(eventData);
    }
  };
  const handleSalaryMinOnBlur = (event: FocusEvent<HTMLInputElement>) => {
    sendSalaryUpdateLinkEvent(true);

    const validationResult = handleSalaryValidation({
      minSalary: minimumSalary,
      maxSalary: maximumSalary,
      maxSalaryText,
      validationType: 'min',
      value: normalizeNumber(event.target.value),
    });

    if (validationResult) {
      const eventData = {
        eventName: FORM_VALIDATION_ERROR,
        rangeType: 'min',
        action: 'blur',
        errorMessage: validationResult,
      };
      getTrackingAdapter.sendLinkEvent(eventData);
    }
  };
  const handleSalaryTextOnBlur = (event: FocusEvent<HTMLInputElement>) => {
    const { value } = event.target;

    const message = handleSalaryTextValidation(value);
    if (!message) {
      dispatch(actionUpdateSalaryText(value));
    }

    const eventData = {
      eventName: PAY_DISPLAYED_PRESSED,
      objectInteraction: 'pay-details',
    };
    getTrackingAdapter.sendLinkEvent(eventData);
  };
  const handlePayShownToggleChange = (event: FormEvent<HTMLInputElement>) => {
    const { value } = event.currentTarget;
    setPayDisplayToggleValue(value as 'show' | 'hide');

    const newDisplayPay = value === 'show';

    dispatch(actionUpdateDisplayPay(newDisplayPay));
    dispatch(actionUpdateTrackingDisplayPay(newDisplayPay));

    if (newDisplayPay && !salaryText) {
      const salaryTextDetails = constructDefaultSalaryDisplayText({
        min: minimumSalary,
        max: maximumSalary,
        currency: salaryCurrency,
        payType,
      });
      setSalaryDetails(salaryTextDetails);

      dispatch(actionUpdateSalaryText(salaryTextDetails));
    }

    const eventData = {
      eventName: TOGGLE_PRESSED,
      toggleType: SALARY_HIDE,
      toggleState: newDisplayPay ? 'off' : 'on',
    };
    getTrackingAdapter.sendLinkEvent(eventData);
  };
  const handleSalaryTextChange = (event: ChangeEvent<HTMLInputElement>) => {
    const { value } = event.target;
    const trimmedValue = shouldRestrictEmojis ? removeEmoji(value) : value;

    setSalaryDetails(trimmedValue);
    handleSalaryTextValidation(trimmedValue);
    dispatchSalaryDetails({
      maxValue: maximumSalary,
      minValue: minimumSalary,
      text: trimmedValue,
    });
  };

  // Side effects 👻
  useEffect(() => {
    if (payTypeOriginal) {
      setPayTypeText(payTypeOriginal);
    }
  }, [payTypeOriginal]);
  useEffect(() => {
    // If any values of min/max, currency or pay type changes then format
    // the text box values and update salary text if its empty
    const minSalText = formatSalaryWithCurrency(minimumSalary, currency);
    const maxSalText = formatSalaryWithCurrency(maximumSalary, currency);
    setMinSalaryText(isPositiveNumber(minimumSalary) ? minSalText : '');
    setMaxSalaryText(isPositiveNumber(maximumSalary) ? maxSalText : '');
    if (!salaryText) {
      const salaryTextDetails = constructDefaultSalaryDisplayText({
        min: minimumSalary,
        max: maximumSalary,
        currency,
        payType,
      });
      dispatch(actionUpdateSalaryText(salaryTextDetails));
    }
    // NOTE: This debliberately excludes watching changes on "salaryText"
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dispatch, currency, payType, maximumSalary, minimumSalary]);

  useEffect(() => {
    if (salaryJudgement === 'TOO_HIGH') {
      const numericCategoryId = parseNumber(categoryId);
      const numericSubCategoryId = parseNumber(subCategoryId);
      const classification = getClassificationDataByAdCenterIds(
        numericCategoryId,
        numericSubCategoryId,
      );
      const eventData = {
        eventName: EVENT_NAME_ALERT_VIEWED,
        alertShownTone: 'cautionary',
        alertShownMessage: absurdSalaryWarning,
        alertShownMessageCode: 'Absurdly high salary message',
        jobTitle,
        jobClassificationId: numericCategoryId,
        jobSubClassificationId: numericSubCategoryId,
        jobClassification: classification?.classificationText,
        jobSubClassification: classification?.subClassificationText,
        jobSalaryType: payType,
      };
      getTrackingAdapter.sendLinkEvent(eventData);
    }
  }, [
    salaryJudgement,
    jobTitle,
    categoryId,
    subCategoryId,
    payType,
    absurdSalaryWarning,
  ]);

  useEffect(() => {
    if (minSalaryRecommended && maxSalaryRecommended) {
      dispatch(
        actionUpdateRecommendedSalaryRange(
          `${minSalaryRecommended}-${maxSalaryRecommended}`,
        ),
      );
    }
  }, [dispatch, maxSalaryRecommended, minSalaryRecommended]);

  return (
    <Stack space="small">
      <PayType handleChange={handlePayTypeChanged} inputValue={payTypeText} />
      <PayRange
        handleSalaryCurrencyOnChange={handleSalaryCurrencyOnChange}
        jobTitle={jobTitle}
        maxSalaryFieldMessage={salaryErrorMax}
        maxSalaryText={maxSalaryText}
        minSalaryFieldMessage={salaryErrorMin}
        minSalaryText={minSalaryText}
        onMaxSalaryFieldBlur={handleSalaryMaxOnBlur}
        onMaxSalaryFieldChange={handleMaxSalaryChange}
        onMinSalaryFieldBlur={handleSalaryMinOnBlur}
        onMinSalaryFieldChange={handleMinSalaryChange}
        salaryError={salaryError}
        currency={currency}
        onToggleContextualHelp={handleToggleContextualHelp}
        suggestedSalary={suggestedSalary}
      />
      {salaryJudgement === 'GOOD' && <GoodSalaryMessage />}
      {salaryJudgement === 'LOW' && (
        <LowSalaryMessage
          jobTitle={jobTitle}
          suggestedSalary={suggestedSalary}
        />
      )}
      {salaryJudgement === 'TOO_HIGH' && (
        <AbsurdSalaryMessage message={absurdSalaryWarning} />
      )}
      {suggestedSalary ? (
        <PayRangeRecommendation
          suggestedSalary={suggestedSalary}
          minSalaryValue={minimumSalary ?? undefined}
          maxSalaryValue={maximumSalary ?? undefined}
          minSalaryRecommended={minSalaryRecommended}
          maxSalaryRecommended={maxSalaryRecommended}
          payType={payType}
        />
      ) : null}
      <PayShownToggle
        isANZ={isANZ}
        validationMessage={payShownValidationMessage}
        payDisplayToggleValue={payDisplayToggleValue}
        handlePayShownToggleChange={handlePayShownToggleChange}
        salaryText={salaryDetails}
        handleSalaryTextOnBlur={handleSalaryTextOnBlur}
        handleSalaryTextChange={handleSalaryTextChange}
        payDisplayToggleAlertText={payDisplayToggleAlertText}
      />
    </Stack>
  );
});
